import {
    type ReactNode,
    type PropsWithChildren,
    createContext,
    createElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signOut as firebaseSignOut,
    sendPasswordResetEmail,
    updateProfile,
    onAuthStateChanged,
} from 'firebase/auth';
import { auth, googleProvider, githubProvider, isFirebaseConfigured } from '../lib/firebase';

export interface AuthUser {
    uid?: string;
    email: string;
    role: string;
    displayName: string;
    photoURL?: string;
    provider: 'email' | 'google' | 'github';
}

export interface AuthContext {
    user: AuthUser | null;
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    signUp: (email: string, password: string, displayName: string) => Promise<{ success: boolean; error?: string }>;
    loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
    loginWithGithub: () => Promise<{ success: boolean; error?: string }>;
    sendPasswordReset: (email: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
    isInitializing: boolean;
    isAuthenticated: boolean;
}

// ─── Demo mode credentials (used when Firebase is not configured) ───────────
const DEMO_CREDENTIALS = [
    { email: 'operator@visioninspect.io', password: 'operator123', role: 'Operator', displayName: 'Operator' },
    { email: 'qcmanager@visioninspect.io', password: 'qcmanager123', role: 'Quality Control Manager', displayName: 'QC Manager' },
    { email: 'admin@visioninspect.io', password: 'admin123', role: 'Admin', displayName: 'Administrator' },
];

const AUTH_STORAGE_KEY = 'visioninspect_auth';

// ─── Map Firebase error codes to friendly messages ──────────────────────────
function firebaseErrorMessage(code: string): string {
    switch (code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
            return 'Invalid email or password. Please try again.';
        case 'auth/email-already-in-use':
            return 'This email is already registered. Try signing in.';
        case 'auth/weak-password':
            return 'Password must be at least 6 characters.';
        case 'auth/invalid-email':
            return 'Please enter a valid email address.';
        case 'auth/popup-closed-by-user':
            return 'Sign-in cancelled. Please try again.';
        case 'auth/network-request-failed':
            return 'Network error. Check your connection and try again.';
        case 'auth/too-many-requests':
            return 'Too many attempts. Please wait a moment and try again.';
        case 'auth/popup-blocked':
            return 'Pop-up was blocked. Please allow pop-ups for this site.';
        default:
            return 'Authentication failed. Please try again.';
    }
}

const AuthReactContext = createContext<AuthContext | undefined>(undefined);

export function useAuth(): AuthContext {
    const ctx = useContext(AuthReactContext);
    if (!ctx) throw new Error('AuthProvider is not present');
    return ctx;
}

export function AuthProvider({ children }: PropsWithChildren<{ children: ReactNode }>) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);

    useEffect(() => {
        if (isFirebaseConfigured && auth) {
            // Real Firebase session listener
            const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
                if (firebaseUser) {
                    setUser({
                        uid: firebaseUser.uid,
                        email: firebaseUser.email ?? '',
                        role: 'Operator',
                        displayName: firebaseUser.displayName ?? firebaseUser.email ?? 'User',
                        photoURL: firebaseUser.photoURL ?? undefined,
                        provider: (firebaseUser.providerData[0]?.providerId === 'google.com'
                            ? 'google'
                            : firebaseUser.providerData[0]?.providerId === 'github.com'
                                ? 'github'
                                : 'email'),
                    });
                } else {
                    setUser(null);
                }
                setIsInitializing(false);
            });
            return unsubscribe;
        } else {
            // Demo mode — restore from localStorage
            try {
                const stored = localStorage.getItem(AUTH_STORAGE_KEY);
                if (stored) {
                    const parsed = JSON.parse(stored) as AuthUser;
                    if (parsed.email) setUser(parsed);
                }
            } catch { /* ignore */ }
            setIsInitializing(false);
        }
    }, []);

    const persistDemoUser = useCallback((authUser: AuthUser) => {
        setUser(authUser);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authUser));
    }, []);

    // ─── Email / password sign-in ────────────────────────────────────────────
    const login = useCallback(async (email: string, password: string) => {
        if (isFirebaseConfigured && auth) {
            try {
                await signInWithEmailAndPassword(auth, email, password);
                return { success: true };
            } catch (err: any) {
                return { success: false, error: firebaseErrorMessage(err.code) };
            }
        }
        // ── Demo fallback ──
        await new Promise(r => setTimeout(r, 800));
        const cred = DEMO_CREDENTIALS.find(
            c => c.email.toLowerCase() === email.toLowerCase() && c.password === password
        );
        if (!cred) return { success: false, error: 'Invalid email or password. Please try again.' };
        persistDemoUser({ email: cred.email, role: cred.role, displayName: cred.displayName, provider: 'email' });
        return { success: true };
    }, [persistDemoUser]);

    // ─── Email / password sign-up ────────────────────────────────────────────
    const signUp = useCallback(async (email: string, password: string, displayName: string) => {
        if (isFirebaseConfigured && auth) {
            try {
                const result = await createUserWithEmailAndPassword(auth, email, password);
                if (displayName) {
                    await updateProfile(result.user, { displayName });
                }
                return { success: true };
            } catch (err: any) {
                return { success: false, error: firebaseErrorMessage(err.code) };
            }
        }
        // ── Demo fallback ──
        await new Promise(r => setTimeout(r, 800));
        persistDemoUser({ email, role: 'Operator', displayName: displayName || 'User', provider: 'email' });
        return { success: true };
    }, [persistDemoUser]);

    // ─── Google sign-in ───────────────────────────────────────────────────────
    const loginWithGoogle = useCallback(async () => {
        if (isFirebaseConfigured && auth && googleProvider) {
            try {
                await signInWithPopup(auth, googleProvider);
                return { success: true };
            } catch (err: any) {
                return { success: false, error: firebaseErrorMessage(err.code) };
            }
        }
        // ── Demo fallback ──
        await new Promise(r => setTimeout(r, 1200));
        persistDemoUser({
            email: 'google.user@gmail.com',
            role: 'Operator',
            displayName: 'Google User',
            photoURL: 'https://lh3.googleusercontent.com/a/default-user',
            provider: 'google',
        });
        return { success: true };
    }, [persistDemoUser]);

    // ─── GitHub sign-in ───────────────────────────────────────────────────────
    const loginWithGithub = useCallback(async () => {
        if (isFirebaseConfigured && auth && githubProvider) {
            try {
                await signInWithPopup(auth, githubProvider);
                return { success: true };
            } catch (err: any) {
                return { success: false, error: firebaseErrorMessage(err.code) };
            }
        }
        // ── Demo fallback ──
        await new Promise(r => setTimeout(r, 1200));
        persistDemoUser({
            email: 'github.user@github.com',
            role: 'Operator',
            displayName: 'GitHub User',
            photoURL: 'https://avatars.githubusercontent.com/u/0',
            provider: 'github',
        });
        return { success: true };
    }, [persistDemoUser]);

    // ─── Password reset email ─────────────────────────────────────────────────
    const sendPasswordReset = useCallback(async (email: string) => {
        if (isFirebaseConfigured && auth) {
            try {
                await sendPasswordResetEmail(auth, email);
                return { success: true };
            } catch (err: any) {
                return { success: false, error: firebaseErrorMessage(err.code) };
            }
        }
        // ── Demo fallback ──
        await new Promise(r => setTimeout(r, 800));
        return { success: true };
    }, []);

    // ─── Sign out ─────────────────────────────────────────────────────────────
    const logout = useCallback(async () => {
        if (isFirebaseConfigured && auth) {
            await firebaseSignOut(auth);
        }
        setUser(null);
        localStorage.removeItem(AUTH_STORAGE_KEY);
    }, []);

    const value = useMemo<AuthContext>(
        () => ({
            user,
            login,
            signUp,
            loginWithGoogle,
            loginWithGithub,
            sendPasswordReset,
            logout,
            isInitializing,
            isAuthenticated: !!user,
        }),
        [user, login, signUp, loginWithGoogle, loginWithGithub, sendPasswordReset, logout, isInitializing]
    );

    return createElement(AuthReactContext.Provider, { value, children });
}
